import {
  describeSettings,
  REGISTER_KIND_LABELS,
  type RegisterConnectorInput,
  type RegisterConnectorSummary,
  type RegisterKind,
  type RegisterRunSummary,
  SCHEDULE_LABELS,
} from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Landmark } from 'lucide-react';
import { useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { LearnMore } from '../components/guidance/learn-more';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { ConnectorForm } from '../components/registers/connector-form';
import { ConnectorStatus } from '../components/registers/connector-status';
import { LobbyForm } from '../components/registers/lobby-form';
import { when } from '../components/scrapers/monitor-status';
import { Button } from '../components/ui/button';
import { useOptionalConfirm } from '../components/ui/confirm';
import { ApiError, api } from '../lib/api';
import { CONNECTORS_KEY, type ConnectorList } from '../lib/registers';

// Admin → Public registers: connectors that fetch official records (European Parliament votes, declared meetings,
// written questions, posts) and write them as entities and claims with the register's page as evidence.

export const Route = createFileRoute('/admin/registers/')({ component: RegistersPage });

const TOUR = {
  id: 'admin-registers',
  steps: [
    {
      target: '[data-tour="register-form"]',
      title: 'Choose what to fetch',
      body: 'Pick the MEPs, votes and dates. Each record becomes a claim with the register’s page as its source.',
      learnMore: 'public-registers',
    },
    {
      target: '[data-tour="register-list"]',
      title: 'Runs and matches',
      body: 'Each connector shows its last run. Open one to see what it fetched and to say who a person is when the name alone was not enough.',
    },
  ],
};

function RegistersPage() {
  useScreenTour(TOUR);
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const confirm = useOptionalConfirm();
  const q = useQuery({ queryKey: CONNECTORS_KEY, queryFn: () => api<ConnectorList>('/admin/registers') });
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [kind, setKind] = useState<RegisterKind>('european_parliament');
  const onError = (e: unknown) => {
    if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
    setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : 'Something went wrong.' });
  };
  const refresh = () => qc.invalidateQueries({ queryKey: CONNECTORS_KEY });

  const create = useMutation({
    mutationFn: (input: RegisterConnectorInput) =>
      withReauth(() => api<{ connector: RegisterConnectorSummary }>('/admin/registers', { body: input })),
    onSuccess: r => {
      setMessage({
        kind: 'ok',
        text:
          r.connector.schedule === 'manual'
            ? `Created “${r.connector.name}”. Press Fetch now to read the registers.`
            : `Created “${r.connector.name}”. Its first run starts within half an hour, or press Fetch now.`,
      });
      setFormKey(k => k + 1);
      refresh();
    },
    onError,
  });
  const run = useMutation({
    mutationFn: (c: RegisterConnectorSummary) =>
      withReauth(() => api<{ run: RegisterRunSummary }>(`/admin/registers/${c.id}/run`, { body: {} })),
    onMutate: () => setMessage(null),
    onSuccess: (_r, c) => setMessage({ kind: 'ok', text: `“${c.name}” is fetching. Its progress shows on your activity card.` }),
    onSettled: () => refresh(),
    onError,
  });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      withReauth(() => api<{ enabled: boolean }>('/admin/register-settings', { method: 'PUT', body: { enabled } })),
    onSuccess: s => {
      setMessage({
        kind: 'ok',
        text: s.enabled
          ? 'Public registers are on again. Due runs start at the next pass.'
          : 'Public registers are off. Nothing is fetched until you switch them on.',
      });
      refresh();
    },
    onError,
  });
  const submit = (input: RegisterConnectorInput) => {
    setMessage(null);
    create.mutate(input);
  };
  const askToggle = async (enabled: boolean) => {
    setMessage(null);
    if (!enabled && confirm) {
      const ok = await confirm({
        title: 'Switch off all public registers?',
        body: 'Nothing is fetched, on schedule or by hand, until they are switched on again. What was fetched stays.',
        confirmLabel: 'Switch off',
        cancelLabel: 'Keep them on',
        destructive: true,
      });
      if (!ok) return;
    }
    toggle.mutate(enabled);
  };

  if (q.isError)
    return (
      <>
        <PageHeader title="Public registers" />
        <ErrorState
          error={q.error}
          title="The public registers couldn’t be loaded"
          onRetry={() => q.refetch()}
          learnMore="public-registers"
        />
      </>
    );
  const connectors = q.data?.connectors ?? [];
  const enabled = q.data?.settings.enabled;

  return (
    <div className="space-y-8">
      <PageHeader
        title="Public registers"
        description="Official records fetched from public registers: European Parliament roll-call votes, declared meetings, written questions and posts in committees and delegations. Each becomes a claim with the register’s page as its source."
      />
      <Tip id="registers-data" learnMore="public-registers">
        Fetched records are data: they are stored and shown, never read as instructions, and never sent to an AI model.
      </Tip>
      {message && (
        <p
          role={message.kind === 'error' ? 'alert' : 'status'}
          className={message.kind === 'error' ? 'text-sm text-danger-foreground' : 'text-sm'}
        >
          {message.text}
        </p>
      )}

      {enabled !== undefined && (
        <section
          aria-labelledby="switch-title"
          className="flex flex-wrap items-center gap-3 rounded-lg border bg-surface-raised p-4 text-sm"
        >
          <div className="flex-1 space-y-1">
            <h2 id="switch-title" className="font-semibold">
              {enabled ? 'Registers are on' : 'Registers are switched off'}
            </h2>
            <p className="text-muted-foreground">
              {enabled
                ? 'Due connectors run every half hour.'
                : 'Nothing is fetched, on schedule or by hand, until registers are switched on again.'}
            </p>
          </div>
          <Button size="sm" variant={enabled ? 'outline' : 'primary'} disabled={toggle.isPending} onClick={() => askToggle(!enabled)}>
            {enabled ? 'Switch off all registers' : 'Switch registers on'}
          </Button>
        </section>
      )}

      <section aria-labelledby="form-title" className="space-y-4" data-tour="register-form">
        <h2 id="form-title" className="font-semibold">
          New {kind === 'lobby_funding' ? 'lobby and funding' : 'European Parliament'} connector
        </h2>
        <fieldset className="flex flex-wrap gap-4 text-sm">
          <legend className="sr-only">Register</legend>
          {(['european_parliament', 'lobby_funding'] as const).map(k => (
            <label key={k} className="flex min-h-6 items-center gap-2">
              <input type="radio" name="register-kind" className="size-4" checked={kind === k} onChange={() => setKind(k)} />
              {REGISTER_KIND_LABELS[k]}
            </label>
          ))}
        </fieldset>
        {kind === 'lobby_funding' ? (
          <LobbyForm key={`lobby-${formKey}`} titleId="form-title" pending={create.isPending} onSubmit={submit} />
        ) : (
          <ConnectorForm key={`ep-${formKey}`} titleId="form-title" pending={create.isPending} onSubmit={submit} />
        )}
      </section>

      <section aria-labelledby="connectors-title" className="space-y-3" data-tour="register-list">
        <h2 id="connectors-title" className="font-semibold">
          Connectors
        </h2>
        {!q.data ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : connectors.length === 0 ? (
          <EmptyState icon={Landmark} title="No register connectors yet" learnMore="public-registers">
            Create one for the MEPs you follow: their votes, the meetings they declare, the questions they sign and their posts.
          </EmptyState>
        ) : (
          <div className="relative overflow-x-auto rounded-lg border bg-surface">
            <table className="w-full text-sm" aria-label="Register connectors">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="p-2 text-start font-medium">Connector</th>
                  <th className="p-2 text-start font-medium">How often</th>
                  <th className="p-2 text-start font-medium">Last run</th>
                  <th className="p-2 text-start font-medium">Next run</th>
                  <th className="p-2 text-start font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {connectors.map(c => (
                  <tr key={c.id} className="border-b last:border-0" aria-label={c.name}>
                    <td className="max-w-sm p-2">
                      <Link to="/admin/registers/$id" params={{ id: c.id }} className="font-medium underline-offset-2 hover:underline">
                        <DataText>{c.name}</DataText>
                      </Link>
                      <span className="block text-xs text-muted-foreground">{describeSettings(c.settings)}</span>
                      {c.pendingMatches > 0 && (
                        <span className="block text-xs text-warning-foreground">
                          <DataNumber>{c.pendingMatches}</DataNumber> {c.pendingMatches === 1 ? 'match' : 'matches'} to check
                        </span>
                      )}
                    </td>
                    <td className="p-2">{SCHEDULE_LABELS[c.schedule]}</td>
                    <td className="p-2" data-testid="connector-status">
                      <ConnectorStatus connector={c} />
                    </td>
                    <td className="p-2">
                      <DataNumber>{c.enabled ? (c.nextRunAt ? when(c.nextRunAt) : 'By hand only') : 'Paused'}</DataNumber>
                    </td>
                    <td className="p-2">
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          context={`for ${c.name}`}
                          disabled={!enabled || c.running || (run.isPending && run.variables?.id === c.id)}
                          onClick={() => run.mutate(c)}
                        >
                          {c.running ? 'Fetching…' : 'Fetch now'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <LearnMore slug="public-registers" />
      </section>
    </div>
  );
}
